const TAB_PREFIX = "grants-";
const TAB_CLASSES = {
  base: "before:content-[''] before:size-[8px] before:bg-white before:mr-1 px-4 py-3 block md:inline-block border-t-1 border-l-1 last:border-r-1 transition-colors",
  active: "bg-black text-white before:inline-block border-black hover:bg-gray",
  inactive: "text-gray before:hidden border-lightborder hover:bg-gray-light",
};

export class TabManager {
  constructor(
    urlManager,
    algoliaManager,
    filterManager,
    grantsTabs,
    performSearchAndRenderCallback,
  ) {
    this.urlManager = urlManager;
    this.algoliaManager = algoliaManager;
    this.filterManager = filterManager;
    this.grantsTabs = grantsTabs;
    this.performSearchAndRender = performSearchAndRenderCallback;
    this.tabPrefix = TAB_PREFIX;

    this.tabState = {
      currentTab: null,
      currentSearchTerm: "",
      setCurrentTab: function (tab, searchTerm = "") {
        this.currentTab = tab;
        this.currentSearchTerm = searchTerm;
        this.notifyStateChange();
      },
      notifyStateChange: function () {},
    };

    this.initTabSwitching();
    this.initClearFilterButton();
    this.handleSingleTabDisplay();
  }

  initTabSwitching() {
    if (!this.grantsTabs || this.grantsTabs.length === 0) return;

    const tabLinks = document.querySelectorAll(`[id^="${this.tabPrefix}"]`);

    tabLinks.forEach((link) => {
      link.addEventListener("click", (e) => this.handleTabClick(e, link));
      link.addEventListener("keydown", (e) => {
        let currentFocus = document.activeElement;
        if (!currentFocus || !currentFocus.matches(`[id^="${this.tabPrefix}"]`))
          return;

        let newFocus = null;
        const currentIndex = Array.from(tabLinks).indexOf(currentFocus);

        if (e.key === "ArrowRight") {
          newFocus = tabLinks[currentIndex + 1] || tabLinks[0];
        } else if (e.key === "ArrowLeft") {
          newFocus =
            tabLinks[currentIndex - 1] || tabLinks[tabLinks.length - 1];
        }

        if (newFocus) {
          e.preventDefault();
          newFocus.focus();
          newFocus.click();
        }
      });
    });

    window.addEventListener("hashchange", () => this.handleHashChange());
    window.addEventListener("popstate", () => this.handleHashChange());
    (async () => {
      await this.setTabFromHash();
      const { tabSlug, searchTerm } = this.urlManager.parseGrantsURL();
      // Scroll to grants database if there's a tab or search term in the URL
      if (tabSlug || searchTerm) {
        this.scrollToGrantsDatabase();
      }
    })();
  }

  initClearFilterButton() {
    const clearFilter = document.getElementById("algolia-clear-button");
    if (clearFilter) {
      clearFilter.addEventListener("click", (e) => {
        e.preventDefault();
        this.filterManager.resetFiltersUI();
        this.algoliaManager.clear(true);
        const searchInput = document.getElementById("grant-search");
        if (searchInput) {
          searchInput.value = "";
        }
        const { tabSlug } = this.urlManager.parseGrantsURL();
        this.urlManager.updateURL(tabSlug, "", "", "", true);
      });
    }
  }

  async handleTabClick(e, linkElement) {
    e.preventDefault();
    const tabSlug = linkElement.id.replace(this.tabPrefix, "");
    const selectedTab = this.grantsTabs.find((tab) => tab.slug === tabSlug);
    if (!selectedTab) return;
    const currentSearchTerm = this.algoliaManager.getAlgoliaCurrentQuery();
    await this.switchToTab(selectedTab, currentSearchTerm);
  }

  async handleHashChange() {
    await this.setTabFromHash();
    const { tabSlug, searchTerm } = this.urlManager.parseGrantsURL();
    // Scroll to grants database if there's a tab or search term in the URL
    if (tabSlug || searchTerm) {
      this.scrollToGrantsDatabase();
    }
  }

  async switchToTab(selectedTab, searchTerm = "", updateURLBar = true) {
    this.tabState.setCurrentTab(selectedTab, searchTerm);
    this.updateTabUI(selectedTab.slug);
    this.updateTabContent(selectedTab);

    const searchInput = document.getElementById("grant-search");
    if (searchInput) {
      searchInput.value = searchTerm;
    }

    this.filterManager.initializeAmountDropdown();
    if (this.filterManager.grantYearSelect) {
      this.filterManager.grantYearSelect.value = "";
    }

    const newFilter = selectedTab.fund
      ? `focus-area:${selectedTab.fund.name}`
      : null;
    this.algoliaManager.setAlgoliaFilters(newFilter);
    this.algoliaManager.setAlgoliaYearFilter("");
    this.algoliaManager.setAlgoliaPriceRangeFilter("");

    await this.filterManager.populateYearDropdown();
    await this.performSearchAndRender(searchTerm, 0, true);

    if (updateURLBar) {
      this.urlManager.updateURL(selectedTab.slug, searchTerm, "", "");
    }
  }

  async setTabFromHash() {
    const { tabSlug, searchTerm, year, amount } =
      this.urlManager.parseGrantsURL();
    let selectedTab = null;

    // Skip tab switching if slug is "featured-grants" (used only for navigation in single-tab scenarios)
    if (tabSlug && tabSlug !== "featured-grants") {
      selectedTab = this.grantsTabs.find((tab) => tab.slug === tabSlug);
    } else if (this.grantsTabs && this.grantsTabs.length > 0) {
      // Default to first tab if no tab is specified in URL
      selectedTab = this.grantsTabs[0];
    }

    if (selectedTab) {
      await this.switchToTab(selectedTab, searchTerm, false);
      const searchInput = document.getElementById("grant-search");
      if (searchInput) {
        searchInput.value = searchTerm;
      }
      // Set year and amount from URL after tab switch
      if (year && this.filterManager.grantYearSelect) {
        this.filterManager.grantYearSelect.value = year;
        this.algoliaManager.setAlgoliaYearFilter(year);
      }
      // Amount is stored as index, not value
      if (amount && this.filterManager.grantAmountSelect) {
        const amountIndex = parseInt(amount, 10);
        if (
          !isNaN(amountIndex) &&
          amountIndex >= 0 &&
          amountIndex < this.filterManager.grantAmountSelect.options.length
        ) {
          this.filterManager.grantAmountSelect.selectedIndex = amountIndex;
          const selectedValue = this.filterManager.grantAmountSelect.value;
          if (selectedValue) {
            this.algoliaManager.setAlgoliaPriceRangeFilter(selectedValue);
          }
        }
      }
      if (year || amount) {
        await this.performSearchAndRender(searchTerm, 0, true);
      }
    } else {
      this.filterManager.initializeAmountDropdown();
      await this.filterManager.populateYearDropdown();
      // Set year and amount from URL after populating dropdowns
      if (year && this.filterManager.grantYearSelect) {
        this.filterManager.grantYearSelect.value = year;
        this.algoliaManager.setAlgoliaYearFilter(year);
      }
      // Amount is stored as index, not value
      if (amount && this.filterManager.grantAmountSelect) {
        const amountIndex = parseInt(amount, 10);
        if (
          !isNaN(amountIndex) &&
          amountIndex >= 0 &&
          amountIndex < this.filterManager.grantAmountSelect.options.length
        ) {
          this.filterManager.grantAmountSelect.selectedIndex = amountIndex;
          const selectedValue = this.filterManager.grantAmountSelect.value;
          if (selectedValue) {
            this.algoliaManager.setAlgoliaPriceRangeFilter(selectedValue);
          }
        }
      }
      if (searchTerm) {
        const searchInput = document.getElementById("grant-search");
        if (searchInput) {
          searchInput.value = searchTerm;
        }
        await this.performSearchAndRender(searchTerm, 0, true);
      } else {
        const searchInput = document.getElementById("grant-search");
        if (searchInput) {
          searchInput.value = "";
        }
        await this.performSearchAndRender("", 0, true);
      }
    }
  }

  updateTabUI(activeTabSlug) {
    const tabLinks = document.querySelectorAll(`[id^="${this.tabPrefix}"]`);
    tabLinks.forEach((link) => {
      const tabSlug = link.id.replace(this.tabPrefix, "");
      const isActive = tabSlug === activeTabSlug;
      link.setAttribute("aria-selected", isActive ? "true" : "false");
      const baseClasses = TAB_CLASSES.base;
      const stateClasses = isActive ? TAB_CLASSES.active : TAB_CLASSES.inactive;
      link.className = `${baseClasses} ${stateClasses}`;
    });
    this.grantsTabs.forEach((tab) => {
      tab.is_active = tab.slug === activeTabSlug;
    });
  }

  updateTabContent(selectedTab) {
    const titleElement = document.querySelector("h3 strong");
    const descriptionContainer = document.querySelector(".js-tab-description");
    const activeTabPanel = document.getElementById("active-tab-panel");

    if (titleElement) {
      titleElement.textContent = selectedTab.headline;
    }
    if (descriptionContainer) {
      if (selectedTab.description) {
        descriptionContainer.innerHTML = selectedTab.description;
        descriptionContainer.style.display = "block";
      } else {
        descriptionContainer.style.display = "none";
      }
    }

    if (activeTabPanel) {
      activeTabPanel.setAttribute(
        "aria-labelledby",
        `grants-${selectedTab.slug}`,
      );
    }
  }

  scrollToGrantsDatabase() {
    // Use requestAnimationFrame to ensure DOM is ready and painted
    requestAnimationFrame(() => {
      const grantsDatabaseContainer = document.getElementById(
        "grant-database-block",
      );

      if (grantsDatabaseContainer) {
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        ).matches;

        // Add a small delay to ensure layout is complete
        setTimeout(() => {
          grantsDatabaseContainer.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
          });
        }, 100);
      }
    });
  }

  handleSingleTabDisplay() {
    // If there's only one tab, hide the tab interface
    if (this.grantsTabs && this.grantsTabs.length === 1) {
      const tabLinks = document.querySelectorAll(`[id^="${this.tabPrefix}"]`);
      const tabContainer =
        tabLinks[0]?.closest('[role="tablist"]') || tabLinks[0]?.parentElement;

      if (tabContainer) {
        tabContainer.style.display = "none";
        tabContainer.setAttribute("aria-hidden", "true");
      }

      // Also hide individual tab links if no container found
      tabLinks.forEach((link) => {
        link.style.display = "none";
        link.setAttribute("aria-hidden", "true");
      });

      // Remove padding and rule from headline and description container
      const headlineElement = document.querySelector("h3 strong");
      const descriptionContainer = document.querySelector(
        ".js-tab-description",
      );

      if (headlineElement) {
        headlineElement.classList.remove(
          "border-t-1",
          "border-lightborder",
          "pt-5",
        );
      }
      if (descriptionContainer) {
        descriptionContainer.classList.remove(
          "border-t-1",
          "border-lightborder",
          "pt-5",
        );
      }
    }
  }
}
